/**
 * Support requests.
 *
 * Same blueprint as the other modules, with one deliberate departure: this list
 * is sorted OLDEST FIRST. Every other list in this console is a ledger, where
 * newest-first is right. This one is a queue of people waiting for a human, and
 * newest-first put the person who had been waiting longest on the last row of
 * the last page. There was an eighteen-day-old open request sitting there with
 * nothing on screen saying so.
 *
 * So the waiting time is the first thing in every row, the oldest wait is a tile
 * of its own, and the default view is the open queue in the order people joined
 * it.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlarmClock,
  CalendarClock,
  Copy,
  Handshake,
  IndianRupee,
  LifeBuoy,
  MessageSquareOff,
  MoreHorizontal,
  PawPrint,
  Store,
  User,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { date_time, inr, title_case } from '@/lib/format';
import { export_support_requests } from '@/features/support-requests/api';
import { use_support_counts, use_support_request_list } from '@/features/support-requests/hooks';
import {
  OPENED_BY_LABELS,
  SUPPORT_KIND_LABELS,
  SUPPORT_SORT_FIELDS,
  SUPPORT_STATUS_LABELS,
  type SupportCounts,
  type SupportRequest,
  type SupportRequestKind,
  type SupportRequestListFilters,
  type SupportRequestStatus,
} from '@/features/support-requests/types';
import { VENDOR_MODULE_TYPE_LABELS } from '@/features/vendors/types';

const SEGMENTS = ['queue', 'stale', 'agreement', 'closed', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<SupportRequestListFilters>> = {
  queue: { statuses: 'OPEN,PROPOSED' },
  // Open and older than a week. Not a status — a promise being broken.
  stale: { waiting_over_days: 7 },
  agreement: { statuses: 'PROPOSED' },
  closed: { statuses: 'RESOLVED,REFUNDED,CANCELLED' },
  all: {},
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  queue: {
    label: 'Open queue',
    title: 'Support queue',
    blurb: 'Everyone still waiting on us, longest wait first',
  },
  stale: {
    label: 'Waiting over a week',
    title: 'Waiting more than a week',
    blurb: 'Open requests nobody has answered in seven days or more',
  },
  agreement: {
    label: 'Awaiting agreement',
    title: 'Awaiting agreement',
    blurb: 'A new slot has been proposed and both sides have to accept it',
  },
  closed: {
    label: 'Closed',
    title: 'Closed requests',
    blurb: 'Resolved, refunded, or withdrawn',
  },
  all: { label: 'All', title: 'Support requests', blurb: 'Every request ever raised' },
};

const STATUS_TONE: Record<
  SupportRequestStatus,
  'success' | 'warning' | 'neutral' | 'destructive' | 'info'
> = {
  OPEN: 'warning',
  PROPOSED: 'info',
  RESOLVED: 'success',
  REFUNDED: 'destructive',
  CANCELLED: 'neutral',
};

const KIND_TONE: Record<SupportRequestKind, 'warning' | 'info' | 'destructive'> = {
  WALKER_REASSIGN: 'warning',
  TRAINER_RESCHEDULE: 'info',
  VENDOR_NO_SHOW: 'destructive',
};

const KIND_OPTIONS: SupportRequestKind[] = [
  'WALKER_REASSIGN',
  'TRAINER_RESCHEDULE',
  'VENDOR_NO_SHOW',
];
const STATUS_OPTIONS: SupportRequestStatus[] = [
  'OPEN',
  'PROPOSED',
  'RESOLVED',
  'REFUNDED',
  'CANCELLED',
];
const OPENED_BY_OPTIONS = ['USER', 'VENDOR', 'SYSTEM'] as const;

/**
 * How long somebody has been waiting, as the thing you read first.
 *
 * Anything past a week is the platform failing a promise, so it is coloured
 * rather than left as another grey number.
 */
function WaitingCell({ request }: { request: SupportRequest }) {
  const d = request.waiting_days;
  const tone = !request.is_open
    ? 'text-muted-foreground'
    : d >= 7
      ? 'text-destructive'
      : d >= 3
        ? 'text-warning'
        : '';
  const label = d === 0 ? 'today' : d === 1 ? '1 day' : `${d} days`;
  return (
    <span className="flex min-w-0 flex-col">
      <span className={`whitespace-nowrap font-semibold ${tone}`} data-testid="waiting-days">
        {request.is_open ? label : `closed in ${label}`}
      </span>
      <span className="truncate text-micro text-muted-foreground">
        {date_time(request.created_at)} ·{' '}
        {OPENED_BY_LABELS[request.opened_by] ?? title_case(request.opened_by)}
      </span>
    </span>
  );
}

export function SupportRequestListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'queue';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [kind, set_kind] = use_url_state('kind', enum_param(KIND_OPTIONS));
  const [status, set_status] = use_url_state('status', enum_param(STATUS_OPTIONS));
  const [opened_by, set_opened_by] = use_url_state('opened_by', enum_param(OPENED_BY_OPTIONS));
  const [has_reason, set_has_reason] = use_url_state('has_reason', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    // Oldest first. See the note at the top of the file.
    sort_param(SUPPORT_SORT_FIELDS, { by: 'created_at', dir: 'asc' }),
  );

  const reset_page = () => set_page(1);

  const filters: SupportRequestListFilters = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      kind: kind || undefined,
      // A hand-picked status beats the segment's set.
      ...(status ? { statuses: undefined, status } : {}),
      opened_by: opened_by || undefined,
      has_reason: has_reason || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [segment, page, page_size, q, kind, status, opened_by, has_reason, from, to],
  );

  const { data, isLoading, isFetching, error, refetch } = use_support_request_list(filters, sort);
  const { data: counts } = use_support_counts();

  /* --------------------------------- chips --------------------------------- */

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    kind && {
      key: 'kind',
      label: 'Kind',
      value: SUPPORT_KIND_LABELS[kind],
      on_clear: () => set_kind(''),
    },
    status && {
      key: 'status',
      label: 'Status',
      value: SUPPORT_STATUS_LABELS[status],
      advanced: true,
      on_clear: () => set_status(''),
    },
    opened_by && {
      key: 'opened_by',
      label: 'Raised by',
      value: OPENED_BY_LABELS[opened_by] ?? opened_by,
      advanced: true,
      on_clear: () => set_opened_by(''),
    },
    has_reason && {
      key: 'has_reason',
      label: 'Reason',
      value: has_reason === 'true' ? 'Given' : 'Missing',
      advanced: true,
      on_clear: () => set_has_reason(''),
    },
    (from || to) && {
      key: 'date',
      label: 'Raised',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_kind('');
    set_status('');
    set_opened_by('');
    set_has_reason('');
    set_from('');
    set_to('');
    reset_page();
  }

  /* -------------------------------- metrics -------------------------------- */

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<SupportRequest>[]>(
    () => [
      {
        id: 'created_at',
        header: 'Waiting',
        meta: { sort_key: 'created_at' },
        cell: ({ row }) => <WaitingCell request={row.original} />,
      },
      {
        id: 'kind',
        header: 'What went wrong',
        meta: { sort_key: 'kind' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex min-w-0 flex-col">
              <span className="flex flex-wrap items-center gap-1">
                <Badge tone={KIND_TONE[r.kind]} size="sm" dot>
                  {SUPPORT_KIND_LABELS[r.kind]}
                </Badge>
                <Badge tone="neutral" size="sm">
                  {VENDOR_MODULE_TYPE_LABELS[
                    r.module_type as keyof typeof VENDOR_MODULE_TYPE_LABELS
                  ] ?? r.module_type}
                </Badge>
              </span>
              {/*
                The reason, in the row. It was only on the detail page, so
                triaging a queue meant opening every item to find out what each
                one was about.
              */}
              {/*
                Capped: a reason may be a thousand characters, and an uncapped
                cell pushed the Status column clean off the right edge of a
                1512px screen — the one column an operator triages on.
              */}
              <span
                className="line-clamp-1 max-w-[20rem] text-micro text-muted-foreground"
                title={r.reason ?? undefined}
              >
                {r.reason ? `“${r.reason}”` : <span className="text-warning">No reason given</span>}
              </span>
            </span>
          );
        },
      },
      {
        id: 'about',
        header: 'About',
        enableSorting: false,
        meta: { width: '13rem' },
        cell: ({ row }) => {
          const b = row.original.booking;
          return (
            <span className="flex min-w-0 flex-col">
              <Link
                to={`/bookings/${b.id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
              >
                {b.service_name ?? 'Service removed'}
              </Link>
              <span className="flex flex-wrap items-center gap-1.5 text-micro text-muted-foreground">
                <CalendarClock aria-hidden="true" className="h-3 w-3" />
                {date_time(b.slot_start)}
                {b.pet && (
                  <>
                    <PawPrint aria-hidden="true" className="h-3 w-3" />
                    {b.pet.name}
                  </>
                )}
              </span>
            </span>
          );
        },
      },
      {
        id: 'customer',
        header: 'Customer',
        enableSorting: false,
        cell: ({ row }) => {
          const b = row.original.booking;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <PersonAvatar path={null} name={b.customer_name} seed={b.user_id} size="sm" />
              <span className="flex min-w-0 flex-col">
                <Link
                  to={`/users/${b.user_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  {b.customer_name ?? 'No name on file'}
                </Link>
                <span className="truncate text-micro text-muted-foreground">
                  {b.customer_phone ?? '—'}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'vendor',
        header: 'Vendor',
        enableSorting: false,
        meta: { width: '12rem' },
        cell: ({ row }) => {
          const b = row.original.booking;
          return (
            <Link
              to={`/vendors/${b.vendor_profile_id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex min-w-0 items-center gap-1.5 truncate underline-offset-2 hover:text-primary hover:underline"
            >
              <Store aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{b.vendor_business_name ?? 'Vendor removed'}</span>
            </Link>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sort_key: 'status' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex flex-wrap items-center gap-1">
              <Badge tone={STATUS_TONE[r.status]} size="sm" dot data-testid={`status-${r.status}`}>
                {SUPPORT_STATUS_LABELS[r.status]}
              </Badge>
              {/* Who still has to say yes, when a slot is on the table. */}
              {r.status === 'PROPOSED' && (
                <Badge tone="neutral" size="sm" className="whitespace-nowrap">
                  <Handshake className="h-3 w-3" />
                  {r.user_agreed && r.trainer_agreed
                    ? 'both agreed'
                    : r.user_agreed
                      ? 'vendor to agree'
                      : r.trainer_agreed
                        ? 'customer to agree'
                        : 'neither yet'}
                </Badge>
              )}
              {r.refund_amount_inr && (
                <Badge tone="destructive" size="sm" className="whitespace-nowrap">
                  {inr(r.refund_amount_inr)} back
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Request actions"
                  data-testid={`row-actions-${r.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(r.id);
                    notify.success('Request ID copied');
                  }}
                  testid={`copy-${r.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy request ID
                </RowAction>
                <DropdownMenuSeparator />
                <RowAction
                  on_select={() => window.open(`/bookings/${r.booking.id}`, '_self')}
                  testid={`open-booking-${r.id}`}
                >
                  <CalendarClock className="h-4 w-4" /> Open the booking
                </RowAction>
                <RowAction
                  on_select={() => window.open(`/users/${r.booking.user_id}`, '_self')}
                  testid={`open-customer-${r.id}`}
                >
                  <User className="h-4 w-4" /> Open the customer
                </RowAction>
                <RowAction
                  on_select={() => window.open(`/vendors/${r.booking.vendor_profile_id}`, '_self')}
                  testid={`open-vendor-${r.id}`}
                >
                  <Store className="h-4 w-4" /> Open the vendor
                </RowAction>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={
        <SavedViews module={`support-requests:${segment}`} is_filtered={chips.some(Boolean)} />
      }
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'queue'
                ? counts
                  ? counts.open + counts.proposed
                  : undefined
                : v === 'stale'
                  ? counts?.open_over_7_days
                  : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<SupportRequest>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(r) => r.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(r) => `/support-requests/${r.id}`}
        storage_key={`support-requests:${segment}`}
        export_name="support-requests"
        on_export_all={() => export_support_requests(filters, sort)}
        caption="Support requests"
        test_id="support-list"
        selectable
        empty={{
          icon: LifeBuoy,
          title: chips.some(Boolean)
            ? 'No requests match these filters'
            : segment === 'queue'
              ? 'Nobody is waiting'
              : segment === 'stale'
                ? 'Nothing has been left over a week'
                : `No ${copy.label.toLowerCase()} requests`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : segment === 'queue'
              ? 'Every support request has been resolved, refunded or withdrawn.'
              : 'Requests appear here when a session falls through, or when a vendor never accepts.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SelectFilter
                  label="Status"
                  value={status}
                  on_change={(v) => {
                    set_status(v as SupportRequestStatus);
                    reset_page();
                  }}
                  options={STATUS_OPTIONS.map((s) => ({
                    value: s,
                    label: SUPPORT_STATUS_LABELS[s],
                  }))}
                  all_label="Any status"
                  test_id="filter-status"
                />
                <SelectFilter
                  label="Raised by"
                  value={opened_by}
                  on_change={(v) => {
                    set_opened_by(v);
                    reset_page();
                  }}
                  options={OPENED_BY_OPTIONS.map((o) => ({
                    value: o,
                    label: OPENED_BY_LABELS[o] ?? o,
                  }))}
                  all_label="Anyone"
                  test_id="filter-opened-by"
                />
                <BoolFilter
                  label="Reason"
                  value={has_reason}
                  on_change={(v) => {
                    set_has_reason(v);
                    reset_page();
                  }}
                  true_label="Given"
                  false_label="Missing"
                  test_id="filter-has-reason"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Customer, vendor, pet, service, reason or ID…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Kind"
              value={kind}
              on_change={(v) => {
                set_kind(v as SupportRequestKind);
                reset_page();
              }}
              options={KIND_OPTIONS.map((k) => ({ value: k, label: SUPPORT_KIND_LABELS[k] }))}
              all_label="Any kind"
              test_id="filter-kind"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Raised"
            />
          </FilterBar>
        }
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: SupportCounts | undefined): Metric[] {
  return [
    {
      key: 'open',
      label: 'Waiting on us',
      value: counts ? (counts.open + counts.proposed).toLocaleString('en-IN') : undefined,
      hint: 'Open or awaiting agreement',
      tone: counts && counts.open + counts.proposed > 0 ? 'warning' : 'default',
      to: '/support-requests?segment=queue',
      icon: LifeBuoy,
      loading: !counts,
    },
    {
      key: 'oldest',
      label: 'Longest wait',
      // The number this module was missing entirely. A count of open requests
      // says nothing about whether somebody is being left.
      value:
        counts?.oldest_open_days === null
          ? 'None'
          : counts
            ? `${counts.oldest_open_days} days`
            : undefined,
      hint: counts?.oldest_open_days ? 'Somebody has been waiting this long' : undefined,
      tone: counts && (counts.oldest_open_days ?? 0) >= 7 ? 'destructive' : 'default',
      to: '/support-requests?segment=queue',
      icon: AlarmClock,
      loading: !counts,
    },
    {
      key: 'stale',
      label: 'Over a week',
      value: counts?.open_over_7_days?.toLocaleString('en-IN'),
      tone: counts && counts.open_over_7_days > 0 ? 'destructive' : 'default',
      to: '/support-requests?segment=stale',
      icon: CalendarClock,
      loading: !counts,
    },
    {
      key: 'no_reason',
      label: 'No reason given',
      value: counts?.open_without_reason?.toLocaleString('en-IN'),
      // An open request with no reason cannot be triaged without ringing somebody.
      hint: 'Open, with nothing to go on',
      tone: counts && counts.open_without_reason > 0 ? 'warning' : 'default',
      to: '/support-requests?segment=queue&has_reason=false',
      icon: MessageSquareOff,
      loading: !counts,
    },
    {
      key: 'refunded',
      label: 'Refunded',
      value: counts ? inr(counts.refunded_inr) : undefined,
      hint: counts ? `${counts.refunded} request${counts.refunded === 1 ? '' : 's'}` : undefined,
      to: '/support-requests?segment=all&status=REFUNDED',
      icon: IndianRupee,
      loading: !counts,
    },
  ];
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}

export default SupportRequestListPage;
